import React, { useMemo, useState } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { CaretLeftIcon, CaretRightIcon, CalendarBlankIcon } from '@phosphor-icons/react';
import { useAthleteSessionsScoped } from '../../CarichiPage/hooks/useAthleteSessionsScoped';
import { ImportBadge } from '../../CarichiPage/components/m6-2/ImportBadge';
import { DataTypeBadge } from '../../CarichiPage/components/m6-2/DataTypeBadge';
import { ISession, SESSION_TYPE, SESSION_SPORT } from '../../../models/sessions';
import { SessionDataType } from '../../../models/m6-2';

dayjs.extend(isoWeek);

// ─── Types ────────────────────────────────────────────────────────────────────

type ScopeFilter = 'all' | 'team' | 'individual';

interface Props {
  athleteId: string;
  athleteName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SESSION_TYPE_LABELS: Record<string, string> = {
  [SESSION_TYPE.TRAINING]: 'Allenamento',
  [SESSION_TYPE.MATCH]: 'Partita',
  [SESSION_TYPE.FRIENDLY]: 'Amichevole',
  [SESSION_TYPE.TEST]: 'Test',
  [SESSION_TYPE.INDIVIDUAL]: 'Individuale',
  [SESSION_TYPE.REHAB]: 'Riabilitazione',
};

const SESSION_SPORT_LABELS: Record<string, string> = {
  [SESSION_SPORT.FOOTBALL]: 'Calcio',
  [SESSION_SPORT.RUGBY]: 'Rugby',
  [SESSION_SPORT.BASKETBALL]: 'Basket',
  [SESSION_SPORT.VOLLEYBALL]: 'Pallavolo',
  [SESSION_SPORT.RUNNING]: 'Corsa',
  [SESSION_SPORT.CYCLING]: 'Ciclismo',
  [SESSION_SPORT.INDOOR_CYCLING]: 'Ciclismo indoor',
  [SESSION_SPORT.MTB]: 'MTB',
  [SESSION_SPORT.GRAVEL]: 'Gravel',
  [SESSION_SPORT.TRAIL_RUNNING]: 'Trail running',
  [SESSION_SPORT.OTHER]: 'Altro',
};

const SCOPE_LABELS: Record<ScopeFilter, string> = {
  all: 'tutte le sessioni',
  team: 'sessioni con la squadra',
  individual: 'sessioni individuali',
};

function formatDuration(minutes: number | undefined): string {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}′`;
  return `${h}h ${m > 0 ? `${m}′` : ''}`;
}

function ScopeIcon({ scope }: { scope: ISession['sessionScope'] }) {
  if (scope === 'team') return <span title="Con la squadra">👥</span>;
  if (scope === 'individual_athlete') return <span title="Individuale">👤</span>;
  return <span style={{ opacity: 0.3 }}>—</span>;
}

// ─── Toggle buttons ───────────────────────────────────────────────────────────

const TAB_OPTIONS: { value: ScopeFilter; label: string }[] = [
  { value: 'all', label: 'Tutte' },
  { value: 'team', label: 'Con la squadra' },
  { value: 'individual', label: 'Individuali' },
];

interface ScopeToggleProps {
  value: ScopeFilter;
  onChange: (v: ScopeFilter) => void;
}

function ScopeToggle({ value, onChange }: ScopeToggleProps) {
  return (
    <HStack gap={2} flexWrap="wrap">
      {TAB_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '5px 14px',
              borderRadius: 20,
              border: '1px solid',
              borderColor: active ? '#013344' : '#e7e7e7',
              background: active ? '#013344' : 'white',
              color: active ? 'white' : '#4d4d4d',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: active ? 700 : 400,
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </HStack>
  );
}

// ─── Table row ────────────────────────────────────────────────────────────────

interface RowProps {
  session: ISession;
  isEven: boolean;
}

function SessionRow({ session, isEven }: RowProps) {
  const dateStr = dayjs(session.date).format('DD/MM/YY');
  const tipo = SESSION_TYPE_LABELS[session.sessionType] ?? session.sessionType;
  const sport = SESSION_SPORT_LABELS[session.sport] ?? session.sport;

  return (
    <tr
      style={{
        background: isEven ? '#f9fafb' : 'white',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--c-text-0)', whiteSpace: 'nowrap' }}>
        {dateStr}
      </td>
      <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--c-text-1)' }}>
        {tipo}
      </td>
      <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--c-text-1)' }}>
        {sport}
      </td>
      <td style={{ padding: '8px 12px', fontSize: 13, color: 'var(--c-text-1)', textAlign: 'right' }}>
        {formatDuration(session.duration)}
      </td>
      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
        <ScopeIcon scope={session.sessionScope} />
      </td>
      <td style={{ padding: '8px 12px' }}>
        {session.dataType && (
          <DataTypeBadge dataType={session.dataType as SessionDataType} compact />
        )}
      </td>
      <td style={{ padding: '8px 12px' }}>
        {session.crossTeamImport && (
          <ImportBadge originalFileTeam={session.originalFileTeam} />
        )}
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

// ─── Week Navigator (Google Calendar pattern) ─────────────────────────────────

interface WeekNavProps {
  weekStart: dayjs.Dayjs;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  isCurrent: boolean;
}

function WeekNav({ weekStart, onPrev, onNext, onToday, isCurrent }: WeekNavProps) {
  const weekEnd = weekStart.endOf('isoWeek');
  const isoWeekNumber = weekStart.isoWeek();
  const sameMonth = weekStart.month() === weekEnd.month();
  const label = sameMonth
    ? `${weekStart.format('DD')}-${weekEnd.format('DD MMM YYYY')}`
    : `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`;

  return (
    <HStack justify="space-between" align="center" w="100%" gap={2}>
      <HStack gap={2}>
        <button
          onClick={onPrev}
          aria-label="Settimana precedente"
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: '1px solid #e7e7e7',
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CaretLeftIcon size={14} weight="bold" />
        </button>
        <HStack
          gap={1}
          px={3}
          h="32px"
          borderRadius={8}
          bg="#f9fafb"
          border="1px solid #e7e7e7"
        >
          <CalendarBlankIcon size={14} />
          <Text fontSize={13} fontWeight={600} fontFamily="'Satoshi', sans-serif">
            W{isoWeekNumber} · {label}
          </Text>
        </HStack>
        <button
          onClick={onNext}
          aria-label="Settimana successiva"
          disabled={isCurrent}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: '1px solid #e7e7e7',
            background: 'white',
            cursor: isCurrent ? 'not-allowed' : 'pointer',
            opacity: isCurrent ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CaretRightIcon size={14} weight="bold" />
        </button>
      </HStack>
      {!isCurrent && (
        <button
          onClick={onToday}
          style={{
            padding: '6px 12px',
            borderRadius: 16,
            border: '1px solid #013344',
            background: 'white',
            color: '#013344',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: "'Satoshi', sans-serif",
          }}
        >
          Torna a oggi
        </button>
      )}
    </HStack>
  );
}

// ─── Week KPI summary ─────────────────────────────────────────────────────────

interface WeekKPI {
  count: number;
  totalDuration: number;
  totalSrpe: number;
}

function WeekSummary({ kpi }: { kpi: WeekKPI }) {
  const items = [
    { label: 'Sessioni', value: String(kpi.count) },
    {
      label: 'Volume totale',
      value: kpi.totalDuration > 0 ? formatDuration(kpi.totalDuration) : '—',
    },
    {
      label: 'sRPE totale',
      value: kpi.totalSrpe > 0 ? Math.round(kpi.totalSrpe).toString() : '—',
    },
  ];
  return (
    <HStack gap={3} flexWrap="wrap">
      {items.map((it) => (
        <Box
          key={it.label}
          flex="1"
          minW="120px"
          px={4}
          py={3}
          borderRadius={10}
          bg="white"
          border="1px solid #e7e7e7"
        >
          <Text
            fontSize={11}
            fontWeight={600}
            color="#8d8d8d"
            textTransform="uppercase"
            letterSpacing="0.05em"
            fontFamily="'Satoshi', sans-serif"
          >
            {it.label}
          </Text>
          <Text
            mt={1}
            fontSize={20}
            fontWeight={700}
            color="#00212c"
            fontFamily="'Satoshi', sans-serif"
          >
            {it.value}
          </Text>
        </Box>
      ))}
    </HStack>
  );
}

export function CarichiAthleteSection({ athleteId, athleteName }: Props) {
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [weekStart, setWeekStart] = useState<dayjs.Dayjs>(() => dayjs().startOf('isoWeek'));

  const { data, isPending, isError } = useAthleteSessionsScoped(athleteId, scope);

  const sessions: ISession[] = Array.isArray(data) ? data : [];

  const currentWeekStart = useMemo(() => dayjs().startOf('isoWeek'), []);
  const isCurrentWeek = weekStart.isSame(currentWeekStart, 'day');

  const weekSessions = useMemo(() => {
    const weekEnd = weekStart.endOf('isoWeek');
    return sessions.filter((s) => {
      const d = dayjs(s.date);
      return (
        (d.isAfter(weekStart) || d.isSame(weekStart, 'day')) &&
        (d.isBefore(weekEnd) || d.isSame(weekEnd, 'day'))
      );
    });
  }, [sessions, weekStart]);

  const weekKpi: WeekKPI = useMemo(
    () => ({
      count: weekSessions.length,
      totalDuration: weekSessions.reduce((acc, s) => acc + (s.duration ?? 0), 0),
      totalSrpe: weekSessions.reduce(
        (acc, s) => acc + ((s as any).internalLoad?.srpe ?? 0),
        0,
      ),
    }),
    [weekSessions],
  );

  return (
    <Box p={4}>
      {/* Header */}
      <VStack align="stretch" gap={3}>
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <Text
            fontWeight={700}
            fontSize={15}
            color="#00212c"
            fontFamily="'Satoshi', sans-serif"
          >
            Carichi{athleteName ? ` · ${athleteName}` : ''}
          </Text>
          {!isPending && !isError && (
            <Text fontSize={12} color="#8d8d8d" fontFamily="'Satoshi', sans-serif">
              {weekSessions.length} session{weekSessions.length === 1 ? 'e' : 'i'} questa settimana
              {' · '}
              {sessions.length} totali
            </Text>
          )}
        </HStack>

        {/* Week navigator */}
        <WeekNav
          weekStart={weekStart}
          onPrev={() => setWeekStart((w) => w.subtract(1, 'week'))}
          onNext={() => setWeekStart((w) => w.add(1, 'week'))}
          onToday={() => setWeekStart(currentWeekStart)}
          isCurrent={isCurrentWeek}
        />

        {/* Week KPI summary */}
        {!isPending && !isError && <WeekSummary kpi={weekKpi} />}

        {/* Scope toggle */}
        <ScopeToggle value={scope} onChange={setScope} />
      </VStack>

      {/* Content */}
      <Box mt={4}>
        {isPending ? (
          <HStack justify="center" py={8}>
            <Spinner size="md" color="#013344" />
            <Text fontSize={13} color="#8d8d8d">
              Caricamento sessioni…
            </Text>
          </HStack>
        ) : isError ? (
          <Box
            textAlign="center"
            py={8}
            px={4}
            borderRadius={8}
            bg="#fef2f2"
            border="1px solid #fecaca"
          >
            <Text fontSize={13} color="#b91c1c">
              Errore nel caricamento delle sessioni. Riprova.
            </Text>
          </Box>
        ) : weekSessions.length === 0 ? (
          <Box
            textAlign="center"
            py={8}
            px={4}
            borderRadius={8}
            bg="#f9fafb"
            border="1px solid #e7e7e7"
          >
            <Text fontSize={13} color="#8d8d8d" fontFamily="'Satoshi', sans-serif">
              Nessuna sessione in questa settimana per {SCOPE_LABELS[scope]}.
              {sessions.length > 0 && ' Naviga indietro per vedere settimane precedenti.'}
            </Text>
          </Box>
        ) : (
          <Box overflowX="auto" borderRadius={8} border="1px solid #e7e7e7">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e7e7e7' }}>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Data
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Tipo
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Sport
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Durata
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Scope
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Dati
                  </th>
                  <th
                    style={{
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Import
                  </th>
                </tr>
              </thead>
              <tbody>
                {weekSessions.map((s, i) => (
                  <SessionRow key={s._id} session={s} isEven={i % 2 === 0} />
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    </Box>
  );
}
